import React from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import {
  useCompleteInteractionFlagMutation,
  useInteractionFlag,
} from "../../crud/interactions/hooks";
import { InteractionFlagNames } from "../../crud/interactions/types";
import { HookCheck } from "../HookCheck";
import { parseError } from "../../apiUtils";
import { analytics } from "../../analytics";

interface UserSourceFormInput {
  source: string;
  useCase: string;
  academicUse: boolean;
}

export const UserSourceDialog = (): React.ReactElement | null => {
  const hasSeenDialog = useInteractionFlag(
    InteractionFlagNames.HasSeenUserSourceDialog,
  );
  const completeHasSeenDialogMutation = useCompleteInteractionFlagMutation(
    InteractionFlagNames.HasSeenUserSourceDialog,
  );

  const { register, handleSubmit, control } = useForm<UserSourceFormInput>({
    mode: "all",
    defaultValues: {
      source: "",
      useCase: "",
      academicUse: false,
    },
  });

  const onSubmit = async (data: UserSourceFormInput) => {
    analytics.track("user-source", {
      source: data.source,
      useCase: data.useCase,
      academicUse: data.academicUse,
    });

    handleClose();
  };

  const handleClose = async () => {
    // Mark INTERACTION_FLAG as completed will close
    await completeHasSeenDialogMutation.mutate({});
  };

  if (hasSeenDialog.isLoading || hasSeenDialog.data?.completed) {
    return null;
  }

  return (
    <Dialog open>
      <DialogTitle sx={{ p: 3 }}>Welcome to Coiled!</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ width: 400 }}>
            <Stack spacing={2}>
              <Typography>How did you learn about Coiled?</Typography>
              <TextField
                variant="outlined"
                multiline
                rows={3}
                {...register("source")}
              />

              <HookCheck
                control={control}
                name="academicUse"
                label="Academic Use"
              />
            </Stack>
          </Box>

          {completeHasSeenDialogMutation.isError && (
            <Typography color="error">
              {parseError(completeHasSeenDialogMutation.error)}
            </Typography>
          )}
          <DialogActions sx={{ mt: 3, p: 0 }}>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Skip
            </Button>
            <Button type="submit">Submit</Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
